import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { useSettings } from "../context/SettingsContext";
import { api, formatPrice } from "../lib/api";
import { ShieldCheck, Truck, CreditCard, CheckCircle2, Tag, Home, Package } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useStore();
  const settings = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", post_code: "", note: "", delivery_method: "address", courier: "econt", office: "" });
  const [payment, setPayment] = useState("cod");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [affiliate, setAffiliate] = useState(null);

  useEffect(() => {
    try { setAffiliate(JSON.parse(localStorage.getItem("pa_affiliate") || "null")); } catch {}
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const couponDiscount = couponInfo?.valid ? couponInfo.discount : 0;
  const affDiscount = affiliate?.discount_percent ? round2(cartTotal * affiliate.discount_percent / 100) : 0;
  const afterDiscount = Math.max(0, round2(cartTotal - couponDiscount - affDiscount));
  const shipCfg = settings?.shipping || { free_threshold: 99, fee: 5 };
  const freeThreshold = shipCfg.free_threshold ?? 99;
  const shipping = afterDiscount >= freeThreshold ? 0 : (shipCfg.fee ?? 5);
  const toFree = round2(Math.max(0, freeThreshold - afterDiscount));
  const total = round2(afterDiscount + shipping);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: coupon, subtotal: cartTotal } });
      setCouponInfo(data);
      if (data.valid) toast.success(`Купонът е приложен: -${formatPrice(data.discount)}`);
      else toast.error(data.message);
    } catch { toast.error("Грешка при проверка"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        ...form, payment_method: payment, total,
        coupon_code: couponInfo?.valid ? couponInfo.code : "",
        affiliate_code: affiliate?.code || "",
        items: cart.map((i) => ({ product_id: i.id, title: i.title, price: i.price, qty: i.qty, image: i.image })),
      });
      setDone({ num: data.order_number, payment });
      clearCart();
      localStorage.removeItem("pa_affiliate");
    } catch { toast.error("Възникна грешка при поръчката."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="order-success">
        <CheckCircle2 size={56} className="text-gold mx-auto mb-6" strokeWidth={1.2} />
        <h1 className="font-serif text-3xl text-navy">Благодарим за поръчката!</h1>
        <p className="text-charcoal/60 mt-3">Номер на поръчка: <span className="text-navy font-semibold" data-testid="order-number">{done.num}</span></p>
        <p className="text-charcoal/60 mt-2">{done.payment === "card" ? "Ще се свържем с вас за плащане с карта и потвърждение." : "Ще се свържем с вас за потвърждение. Плащане при доставка (наложен платеж)."}</p>
        <Link to="/kartini" className="btn-gold inline-block px-8 py-3 rounded-sm mt-8">Към картините</Link>
      </div>
    );
  }

  if (cart.length === 0) {
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><p className="text-charcoal/60">Количката е празна.</p><Link to="/kartini" className="btn-gold inline-block px-6 py-3 rounded-sm mt-6">Разгледай картините</Link></div>;
  }

  const cardEnabled = settings?.card_enabled !== false;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="checkout-page">
      <h1 className="font-serif text-3xl md:text-4xl text-navy mb-8">Завършване на поръчка</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-stone p-6">
            <h2 className="font-serif text-xl text-navy mb-4">Данни за контакт</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Име и фамилия *" value={form.name} onChange={set("name")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold" data-testid="checkout-name" />
              <input required placeholder="Телефон *" value={form.phone} onChange={set("phone")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold" data-testid="checkout-phone" />
              <input type="email" placeholder="Имейл" value={form.email} onChange={set("email")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold sm:col-span-2" data-testid="checkout-email" />
            </div>
          </div>

          <div className="bg-white border border-stone p-6">
            <h2 className="font-serif text-xl text-navy mb-4">Начин на доставка</h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <label className={`flex items-center gap-3 border p-4 cursor-pointer ${form.delivery_method === "address" ? "border-gold bg-ivory" : "border-stone"}`} data-testid="delivery-address">
                <input type="radio" checked={form.delivery_method === "address"} onChange={() => setForm({ ...form, delivery_method: "address" })} className="accent-[#C6A45C]" />
                <Home size={18} className="text-gold" />
                <span className="text-navy font-medium text-sm">До личен адрес</span>
              </label>
              <label className={`flex items-center gap-3 border p-4 cursor-pointer ${form.delivery_method === "office" ? "border-gold bg-ivory" : "border-stone"}`} data-testid="delivery-office">
                <input type="radio" checked={form.delivery_method === "office"} onChange={() => setForm({ ...form, delivery_method: "office" })} className="accent-[#C6A45C]" />
                <Package size={18} className="text-gold" />
                <span className="text-navy font-medium text-sm">До офис на куриер</span>
              </label>
            </div>

            {form.delivery_method === "office" ? (
              <div className="space-y-4" data-testid="office-fields">
                <div>
                  <p className="text-sm text-navy font-medium mb-2">Куриер</p>
                  <div className="flex gap-3">
                    {[["econt", "Еконт"], ["speedy", "Спиди"]].map(([v, label]) => (
                      <label key={v} className={`flex-1 flex items-center justify-center gap-2 border py-2.5 cursor-pointer text-sm ${form.courier === v ? "border-gold bg-ivory text-navy font-medium" : "border-stone text-charcoal/60"}`} data-testid={`courier-${v}`}>
                        <input type="radio" checked={form.courier === v} onChange={() => setForm({ ...form, courier: v })} className="accent-[#C6A45C]" /> {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Град *" value={form.city} onChange={set("city")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold" data-testid="checkout-city" />
                  <input required placeholder="Офис на куриера *" value={form.office} onChange={set("office")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold" data-testid="checkout-office" />
                </div>
                <p className="text-xs text-charcoal/45">Посочете града и конкретния офис на {form.courier === "speedy" ? "Спиди" : "Еконт"} за получаване.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="address-fields">
                <input required placeholder="Град *" value={form.city} onChange={set("city")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold" data-testid="checkout-city" />
                <input placeholder="Пощенски код" value={form.post_code} onChange={set("post_code")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold" data-testid="checkout-postcode" />
                <input required placeholder="Адрес (ул., бл., вх., ап.) *" value={form.address} onChange={set("address")} className="border border-stone px-4 py-2.5 outline-none focus:border-gold sm:col-span-2" data-testid="checkout-address" />
              </div>
            )}
            <textarea placeholder="Бележка към поръчката (по избор)" value={form.note} onChange={set("note")} rows={2} className="w-full border border-stone px-4 py-2.5 outline-none focus:border-gold mt-4" data-testid="checkout-note" />
          </div>

          <div className="bg-white border border-stone p-6">
            <h2 className="font-serif text-xl text-navy mb-4">Начин на плащане</h2>
            <label className={`flex items-center gap-3 border p-4 cursor-pointer ${payment === "cod" ? "border-gold bg-ivory" : "border-stone"}`} data-testid="payment-cod">
              <input type="radio" checked={payment === "cod"} onChange={() => setPayment("cod")} className="accent-[#C6A45C]" />
              <Truck size={18} className="text-gold" />
              <div><span className="text-navy font-medium">Наложен платеж</span><p className="text-xs text-charcoal/50">Плащане в брой при доставка</p></div>
            </label>
            {cardEnabled && (
              <label className={`flex items-center gap-3 border p-4 cursor-pointer mt-3 ${payment === "card" ? "border-gold bg-ivory" : "border-stone"}`} data-testid="payment-card">
                <input type="radio" checked={payment === "card"} onChange={() => setPayment("card")} className="accent-[#C6A45C]" />
                <CreditCard size={18} className="text-gold" />
                <div><span className="text-navy font-medium">Плащане с карта</span><p className="text-xs text-charcoal/50">Ще се свържем с вас за защитено плащане с карта</p></div>
              </label>
            )}
          </div>
        </div>

        <div className="bg-white border border-stone p-6 h-fit">
          <h2 className="font-serif text-xl text-navy mb-4">Вашата поръчка</h2>
          <div className="space-y-3 max-h-56 overflow-y-auto">
            {cart.map((i) => (
              <div key={i.id} className="flex gap-3 text-sm">
                <img src={i.image} alt="" className="w-12 h-14 object-cover bg-stone" />
                <div className="flex-1"><p className="text-navy line-clamp-1">{i.title}</p><p className="text-charcoal/50 text-xs">{i.qty} × {formatPrice(i.price)}</p></div>
              </div>
            ))}
          </div>

          {/* coupon */}
          <div className="flex gap-2 mt-4">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Промо код" className="flex-1 border border-stone px-3 py-2 text-sm outline-none focus:border-gold" data-testid="coupon-input" />
            <button type="button" onClick={applyCoupon} className="btn-navy-outline px-4 text-sm rounded-sm flex items-center gap-1" data-testid="apply-coupon-btn"><Tag size={14} /> Приложи</button>
          </div>

          <div className="mt-4 space-y-1.5 text-sm border-t border-stone pt-3">
            <div className="flex justify-between"><span className="text-charcoal/60">Междинна сума</span><span className="text-navy">{formatPrice(cartTotal)}</span></div>
            {couponDiscount > 0 && <div className="flex justify-between text-green-700"><span>Купон {couponInfo.code}</span><span>-{formatPrice(couponDiscount)}</span></div>}
            {affDiscount > 0 && <div className="flex justify-between text-green-700"><span>Партньорска отстъпка</span><span>-{formatPrice(affDiscount)}</span></div>}
            <div className="flex justify-between"><span className="text-charcoal/60">Доставка</span>
              {shipping === 0
                ? <span className="text-gold font-medium">Безплатна</span>
                : <span className="text-navy">{formatPrice(shipping)}</span>}
            </div>
            {shipping > 0 && (
              <div className="text-xs text-gold-dark bg-gold/10 border border-gold/30 rounded-sm px-2.5 py-1.5 mt-1" data-testid="free-shipping-note">
                Добавете още {formatPrice(toFree)} за <b>безплатна доставка</b>!
              </div>
            )}
          </div>
          <div className="flex justify-between font-serif text-lg py-3 border-t border-stone mt-2"><span className="text-navy">Общо</span><span className="text-navy" data-testid="checkout-total">{formatPrice(total)}</span></div>
          <button type="submit" disabled={submitting} className="btn-gold w-full py-3.5 rounded-sm" data-testid="place-order-btn">
            {submitting ? "Изпращане..." : "Поръчай"}
          </button>
          <p className="text-xs text-charcoal/50 mt-3 flex items-center gap-1.5"><ShieldCheck size={13} className="text-gold" /> Сигурна и защитена поръчка</p>
        </div>
      </form>
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }
